import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import styles from './style.module.css';
import { IoMdSend } from 'react-icons/io';
import { IoMdAttach } from 'react-icons/io';
import { IoMdClose } from 'react-icons/io';
import { IoMdCloudUpload } from 'react-icons/io';
import { assumeConversation, deleteRouting, getBotByCompanyId, sendCompletion, getPastDuesByDocument, getWhatsappHistoryBatch } from '../../../services/kollectaApi';
import FileMessage from '../../../Components/FileMessage';

const HumanChat = () => {
    const location = useLocation();
    const { personalNumber } = useParams();
    const navigate = useNavigate();
    const chatContainerRef = useRef(null);

    const [conversationMessages, setConversationMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [iaActive, setIaActive] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [botData, setBotData] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [pastDueData, setPastDueData] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [hasRecentActivity, setHasRecentActivity] = useState(true);
    const [historyMessages, setHistoryMessages] = useState([]);
    const [lastMessageCount, setLastMessageCount] = useState(0);
    const [allMessages, setAllMessages] = useState([]);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationType, setNotificationType] = useState('success'); // 'success' ou 'error'
    const [notificationMessage, setNotificationMessage] = useState('');

    const getRouting = () => {
        if (location.state?.routing) return location.state.routing;
        const stored = localStorage.getItem('routing');
        return stored ? JSON.parse(stored) : {};
    };

    const routing = getRouting();
    const { customerName = '', email = '', companyId = '', phone = '' } = routing;


    const formatPhoneNumber = (cellphone) => {
        let cleaned = cellphone.replace(/\D/g, '');

        // Se já tem código do país (55) e 13 dígitos, retorna como está
        if (cleaned.startsWith('55') && cleaned.length === 13) {
            return cleaned;
        }

        // Se já tem código do país (55) e 12 ou 14 dígitos, retorna como está
        if (cleaned.startsWith('55') && (cleaned.length === 12 || cleaned.length === 14)) {
            return cleaned;
        }

        // Para números de 11 dígitos (ex: 51981860646) - já tem o 9
        if (cleaned.length === 11 && cleaned.startsWith(/\d{2}9/)) {
            return '55' + cleaned;
        }

        // Para números de 10 dígitos (ex: 5181860646) - sem o 9
        if (cleaned.length === 10) {
            return '55' + cleaned;
        }

        // Para números de 11 dígitos no formato DDD + 9 + 8 dígitos (ex: 11920855534)
        if (cleaned.length === 11 && /^(\d{2})9(\d{8})$/.test(cleaned)) {
            return '55' + cleaned;
        }

        return cleaned;
    };


    const fetchBotData = useCallback(async () => {
        if (!companyId) return;
        
        try {
            console.log("Buscando dados do bot para companyId:", companyId);
            const response = await getBotByCompanyId(companyId);
            console.log("Dados do bot recebidos:", response.data);
            setBotData(response.data);
        } catch (error) {
            console.error("Erro ao buscar dados do bot:", error);
        }
    }, [companyId]);

 
    const fetchPastDueData = useCallback(async () => {
        if (!personalNumber || !routing.companyDisplayName) return;
        
        try {
            console.log("Buscando dados da dívida para personalNumber:", personalNumber);
            const response = await getPastDuesByDocument(personalNumber, routing.companyDisplayName.replace(/\s+/g, ''));
            console.log("Dados da dívida recebidos:", response.data);
            
            if (response.data && response.data.length > 0) {
                setPastDueData(response.data[0]); // Pega a primeira dívida
            }
        } catch (error) {
            console.error("Erro ao buscar dados da dívida:", error);
        }
    }, [personalNumber, routing.companyDisplayName]);

    
    const getPriorityPhoneNumber = useCallback(() => {
        // Prioridade: confirmedNumber > cellPhone > phone (com correção automática)
        let selectedNumber = pastDueData?.confirmedNumber || pastDueData?.cellPhone || phone;
        
        // Se o número tem 12 dígitos e começa com 55, adiciona o 9
        if (selectedNumber && selectedNumber.length === 12 && selectedNumber.startsWith('55')) {
            // Formato: 551120855534 -> 5511920855534
            const ddd = selectedNumber.substring(2, 4); // 11
            const restOfNumber = selectedNumber.substring(4); // 20855534
            selectedNumber = `55${ddd}9${restOfNumber}`;
            console.log(`Número corrigido automaticamente: ${pastDueData?.confirmedNumber || pastDueData?.cellPhone || phone} -> ${selectedNumber}`);
        }
        
        return selectedNumber;
    }, [pastDueData, phone]);

    
    const getAllPhoneNumbers = useCallback(() => {
        
        const savedBatchNumbers = localStorage.getItem("batchPhoneNumbers");
        if (savedBatchNumbers) {
            try {
                const phoneNumbers = JSON.parse(savedBatchNumbers);
                if (Array.isArray(phoneNumbers) && phoneNumbers.length > 0) {
                    console.log('Usando números salvos do batch:', phoneNumbers);
                    return phoneNumbers;
                }
            } catch (error) {
                console.error('Erro ao parsear números do batch:', error);
            }
        }

        
        const phoneNumbers = new Set();

        
        if (phone) {
            phoneNumbers.add(formatPhoneNumber(phone));
        }

        
        if (pastDueData?.confirmedNumber) {
            phoneNumbers.add(formatPhoneNumber(pastDueData.confirmedNumber));
        }
        
        if (pastDueData?.cellPhone) {
            phoneNumbers.add(formatPhoneNumber(pastDueData.cellPhone));
        }

        
        if (pastDueData?.phones && Array.isArray(pastDueData.phones)) {
            pastDueData.phones.forEach(phoneNumber => {
                phoneNumbers.add(formatPhoneNumber(phoneNumber));
            });
        }

        console.log('Usando números do fallback (pastDueData):', Array.from(phoneNumbers));
        return Array.from(phoneNumbers);
    }, [phone, pastDueData]);


   
    const loadMessageHistory = useCallback(async () => {
        if (!botData || botData.length === 0) return;

        console.log('📡 loadMessageHistory chamado');

        try {
            const phoneNumbersArray = getAllPhoneNumbers();
            
            if (phoneNumbersArray.length === 0) return;

            const response = await getWhatsappHistoryBatch({
                bot: botData[0].name,
                cellphones: phoneNumbersArray,
                limit: 10000
            });

            
            if (response.data?.hasRecentActivity !== undefined) {
                setHasRecentActivity(response.data.hasRecentActivity);
            }

            if (response.data?.data && Array.isArray(response.data.data)) {
                const newMessages = response.data.data.reverse();
                const currentMessageCount = newMessages.length;
                
                console.log(`📊 Histórico: ${currentMessageCount} mensagens (anterior: ${lastMessageCount})`);
                
                // Log da estrutura das mensagens do histórico
                if (currentMessageCount > lastMessageCount && lastMessageCount > 0) {
                    console.log('🔍 Estrutura das novas mensagens do histórico:');
                    const newMsgs = newMessages.slice(-(currentMessageCount - lastMessageCount));
                    newMsgs.forEach((msg, index) => {
                        console.log(`  Mensagem ${index + 1}:`, {
                            text: msg.text ? msg.text.substring(0, 50) + '...' : 'undefined',
                            type: msg.type,
                            createdAt: msg.createdAt,
                            isFile: msg.isFile,
                            fileName: msg.fileName,
                            fileType: msg.fileType
                        });
                    });
                }
                
                if (currentMessageCount !== lastMessageCount) {
                    if (currentMessageCount > lastMessageCount && lastMessageCount > 0) {
                        console.log(`🆕 ${currentMessageCount - lastMessageCount} nova(s) mensagem(ns) detectada(s)!`);
                    }
                    setHistoryMessages(newMessages);
                    setLastMessageCount(currentMessageCount);
                    
                    setConversationMessages(prev => {
                        return prev.filter(convMsg => {
                           
                            if (!convMsg.isLocal) return true;
                            
                            const normalizedConvText = convMsg.text?.trim() || '';
                            const existsInHistory = newMessages.some(histMsg => {
                                const normalizedHistText = histMsg.text?.trim() || '';
                                const textsMatch = normalizedConvText === normalizedHistText;
                                const typesMatch = histMsg.type === convMsg.type;
                                
                                return textsMatch && typesMatch;
                            });
                            
                            if (existsInHistory) {
                                console.log('🗑️ Removendo mensagem local duplicada:', normalizedConvText.substring(0, 30));
                            }
                            
                            return !existsInHistory;
                        });
                    });
                }
            } else {
                setHistoryMessages([]);
                setLastMessageCount(0);
            }
        } catch (error) {
            console.error('Erro ao carregar histórico de mensagens:', error);
            setHistoryMessages([]);
        }
    }, [botData, getAllPhoneNumbers, lastMessageCount]);



    useEffect(() => {
        if (!botData || botData.length === 0 || iaActive) return;

        const interval = setInterval(async () => {
            try {
                await loadMessageHistory();
            } catch (error) {
                console.error('Erro no polling automático:', error);
            }
        }, 3000); 

        return () => clearInterval(interval);
    }, [botData, iaActive, loadMessageHistory]);

    
    const combineMessages = useCallback((historyMsgs, conversationMsgs) => {
        const filteredConversationMsgs = conversationMsgs.filter(convMsg => {
           
            if (convMsg.isLocal) {
                const normalizedConvText = convMsg.text?.trim() || '';
                const existsInHistory = historyMsgs.some(histMsg => {
                    if (convMsg.isFile) {
                        const isFileMessage = histMsg.isFile || 
                                            (histMsg.type === 'answer' && 
                                             histMsg.text && 
                                             histMsg.text.startsWith('data:'));
                        if (!isFileMessage) return false;
                       
                        const timeDiff = Math.abs(new Date(histMsg.createdAt).getTime() - new Date(convMsg.createdAt).getTime());
                        return isFileMessage && timeDiff < 60000; 
                    }
                    
                    const normalizedHistText = histMsg.text?.trim() || '';
                    return normalizedConvText === normalizedHistText && histMsg.type === convMsg.type;
                });
                return !existsInHistory;
            }
            
            if (convMsg.isFile) {                
                const existsInHistory = historyMsgs.some(histMsg => {
                    const isFileMessage = histMsg.isFile || 
                                        (histMsg.type === 'answer' && 
                                         histMsg.text && 
                                         histMsg.text.startsWith('data:'));
                    
                    if (!isFileMessage) return false;
                    
                    const timeDiff = Math.abs(new Date(histMsg.createdAt).getTime() - new Date(convMsg.createdAt).getTime());
                    const isRecent = timeDiff < 30000; // 30 segundos de tolerância
                    
                    return isFileMessage && isRecent;
                });
              
                return !existsInHistory;
            }
            
            const normalizedConvText = convMsg.text?.trim() || '';
            const existsInHistory = historyMsgs.some(histMsg => {
                const normalizedHistText = histMsg.text?.trim() || '';
                const textsMatch = normalizedConvText === normalizedHistText;
                const typesMatch = histMsg.type === convMsg.type;
                
                const timeDiff = Math.abs(new Date(histMsg.createdAt).getTime() - new Date(convMsg.createdAt).getTime());
                const isRecent = timeDiff < 10000; // 10 segundos de tolerância
                
                return textsMatch && typesMatch && isRecent;
            });
            
            return !existsInHistory;
        });
        
        
        const allMessages = [...historyMsgs, ...filteredConversationMsgs];
        
        return allMessages.sort((a, b) => {
            const timeA = new Date(a.createdAt).getTime();
            const timeB = new Date(b.createdAt).getTime();
            return timeA - timeB;
        });
    }, []);

   
    useEffect(() => {
        const combined = combineMessages(historyMessages, conversationMessages);
        setAllMessages(combined);
    }, [historyMessages, conversationMessages, combineMessages]);

    useEffect(() => {
        if (location.state?.messages) {
            setConversationMessages(location.state.messages);
        }
    }, [location.state]);

    
    useEffect(() => {
        fetchBotData();
    }, [fetchBotData]);

   
    useEffect(() => {
        fetchPastDueData();
    }, [fetchPastDueData]);

    
    useEffect(() => {
        if (botData && botData.length > 0) {
            loadMessageHistory();
        }
    }, [loadMessageHistory, botData]);

   
    useEffect(() => {
        try {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                setCurrentUser(JSON.parse(storedUser));
            }
        } catch (e) {
            console.warn('Não foi possível ler o usuário do localStorage');
        }
    }, []);

   
    useEffect(() => {
        if (allMessages.length > 0 && chatContainerRef.current) {
            setTimeout(() => {
                if (chatContainerRef.current) {
                    chatContainerRef.current.scrollTo({
                        top: chatContainerRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }, 200);
        }
    }, [allMessages]);

    const handleSendMessage = async () => {
        if (!newMessage.trim() || iaActive || isSending) return;
        
        setIsSending(true);
        
        
        const userMsg = {
            text: newMessage,
            type: 'answer',
            createdAt: new Date(),
            isLocal: true, 
        };
        setConversationMessages(prev => [...prev, userMsg]);
        
        
        setTimeout(() => {
            if (chatContainerRef.current) {
                chatContainerRef.current.scrollTo({
                    top: chatContainerRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }, 50);
        
        const messageToSend = newMessage;
        setNewMessage(''); 
        
        try {
           
            if (!botData || botData.length === 0) {
                console.error("Dados do bot não disponíveis");
                setIsSending(false);
                return;
            }
            
            
            const cellphoneToUse = getPriorityPhoneNumber();
            
            console.log("Números disponíveis para sendCompletion:");
            console.log("- confirmedNumber:", pastDueData?.confirmedNumber);
            console.log("- cellPhone:", pastDueData?.cellPhone);
            console.log("- phone (routing):", phone);
            console.log("- Número escolhido:", cellphoneToUse);
            
            if (!cellphoneToUse) {
                console.error("Número do celular não disponível nos dados da dívida nem no routing");
                setIsSending(false);
                return;
            }
            
            const bot = botData[0];
            const requestBody = {
                question: messageToSend,
                cellphone: cellphoneToUse,
                botName: bot.name,
                metaToken: "96c90d4d39453252acf50a1cc16ded49",
                sendMessage: true,
                companyNumber: bot.botNumber
            };
            
            console.log("Enviando para sendCompletion:", requestBody);
            
            const response = await sendCompletion(requestBody);
            console.log("Resposta do sendCompletion:", response.data);
            
           
            if (response.data && response.data.response) {
                const botMsg = {
                    text: response.data.response,
                    type: 'assistant',
                    createdAt: new Date(),
                };
                setConversationMessages(prev => [...prev, botMsg]);
                
                
                setTimeout(() => {
                    if (chatContainerRef.current) {
                        chatContainerRef.current.scrollTo({
                            top: chatContainerRef.current.scrollHeight,
                            behavior: 'smooth'
                        });
                    }
                }, 100);
            }
            
           
            setTimeout(() => {
                loadMessageHistory();
            }, 500); 
            
        } catch (error) {
            console.error("Erro ao enviar mensagem:", error);
            
        } finally {
            setIsSending(false);
        }
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    
    const validateFile = (file) => {
        const allowedTypes = [
            'application/pdf',    // PDF
            'image/jpeg',         // JPEG
            'image/jpg',          // JPG (alguns navegadores usam image/jpg)
            'image/png'           // PNG
        ];
        
        const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
        
        const maxSize = 2 * 1024 * 1024; // 2MB em bytes
        
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        
        console.log('Validando arquivo:', {
            name: file.name,
            type: file.type,
            size: file.size,
            extension: fileExtension,
            maxSize: maxSize
        });
        
        if (!allowedExtensions.includes(fileExtension)) {
            showNotificationAlert('error', 'Formato de arquivo não permitido!\nApenas PDF, JPEG e PNG são aceitos.');
            return false;
        }
        
        if (!allowedTypes.includes(file.type)) {
            showNotificationAlert('error', 'Formato de arquivo não permitido!\nApenas PDF, JPEG e PNG são aceitos.');
            return false;
        }
        
        if (file.size > maxSize) {
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
            showNotificationAlert('error', `Arquivo muito grande!\nTamanho: ${fileSizeMB}MB\nMáximo permitido: 2MB`);
            return false;
        }
        
        console.log('✅ Arquivo válido:', file.name);
        return true;
    };

    
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        
        
        if (files.length > 1) {
            showNotificationAlert('error', 'Apenas 1 arquivo por vez\né permitido!');
            return;
        }
        
 
        const file = files[0];
        if (file) {
            const isValidFormat = validateFile(file);
            if (!isValidFormat) {
                return; 
            }
        }
        
        
        if (selectedFiles.length > 0) {
            setSelectedFiles(files);
        } else {
            setSelectedFiles(prev => [...prev, ...files]);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const files = Array.from(e.dataTransfer.files);
            
          
            if (files.length > 1) {
                showNotificationAlert('error', 'Apenas 1 arquivo por vez\né permitido!');
                return;
            }
            
            
            const file = files[0];
            if (file) {
                const isValidFormat = validateFile(file);
                if (!isValidFormat) {
                    return; 
                }
            }
            
            
            if (selectedFiles.length > 0) {
                setSelectedFiles(files);
            } else {
                setSelectedFiles(prev => [...prev, ...files]);
            }
        }
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) return;
        
        setIsUploading(true);
        
        try {
            const file = selectedFiles[0]; // Pega o primeiro arquivo
            
            console.log('Arquivo selecionado para upload:', file);
            
            if (!botData || botData.length === 0) {
                console.error("Dados do bot não disponíveis");
                showNotificationAlert('error', 'Dados do bot não disponíveis!');
                return;
            }
            
            const base64 = await convertFileToBase64(file);
            console.log('Arquivo convertido para base64, tamanho:', base64.length);
            
            const maxBase64Size = 2 * 1024 * 1024; // 2MB
            if (base64.length > maxBase64Size) {
                showNotificationAlert('error', 'Arquivo muito grande para upload!\nTamanho do base64: ' + formatFileSize(base64.length));
                return;
            }
            
            const bot = botData[0];
            const cellphoneToUse = getPriorityPhoneNumber();
            
            if (!cellphoneToUse) {
                console.error("Número do celular não disponível");
                showNotificationAlert('error', 'Número do celular não disponível!');
                return;
            }
            
            const requestBody = {
                question: base64,                    // BASE64 do arquivo (não texto)
                cellphone: cellphoneToUse,          // Número do celular
                botName: bot.name,                  // Nome do bot
                metaToken: "96c90d4d39453252acf50a1cc16ded49",
                sendMessage: true,                  // Flag para enviar mensagem
                companyNumber: bot.botNumber,       // Número da empresa
                isFile: true                        // Flag para indicar que é arquivo
            };
            
            console.log("📤 Enviando arquivo para sendCompletion:");
            console.log("- fileName:", file.name);
            console.log("- fileType:", file.type);
            console.log("- fileSize:", formatFileSize(file.size));
            console.log("- base64Length:", base64.length);
            console.log("- cellphone:", cellphoneToUse);
            console.log("- botName:", bot.name);
            console.log("- isFile:", true);
            console.log("- question: [BASE64 -", base64.length, "caracteres]");
            console.log("- requestBody completo:", {
                ...requestBody,
                question: `[BASE64 - ${base64.length} caracteres]`
            });
            
            let response;
            try {
                response = await sendCompletion(requestBody);
                console.log("✅ Sucesso! Backend aceita isFile: true");
                console.log("Resposta do sendCompletion:", response.data);
            } catch (error) {
                console.log("❌ Erro com isFile: true:", error.response?.status);
                console.log("🔄 Tentando fallback sem isFile...");
                
                const fallbackBody = {
                    question: `[ARQUIVO] ${file.name} (${formatFileSize(file.size)})`,
                    cellphone: cellphoneToUse,
                    botName: bot.name,
                    metaToken: "96c90d4d39453252acf50a1cc16ded49",
                    sendMessage: true,
                    companyNumber: bot.botNumber
                };
                
                console.log("🔄 Fallback body:", fallbackBody);
                response = await sendCompletion(fallbackBody);
                console.log("✅ Fallback funcionou!");
                console.log("Resposta do fallback:", response.data);
            }
            
            showNotificationAlert('success', 'O upload do arquivo\nfoi feito com sucesso!');
            
            const fileMsg = {
                text: base64, // Usar o base64 real do arquivo
                type: 'answer',
                createdAt: new Date(),
                isFile: true,
                fileName: file.name,
                fileType: file.type,
                isLocal: true, 
            };
            
            console.log('📤 Adicionando arquivo à conversa:', file.name);
            setConversationMessages(prev => {
                const newMessages = [...prev, fileMsg];
                console.log('📝 conversationMessages agora tem:', newMessages.length, 'mensagens');
                return newMessages;
            });
            
            if (response.data && response.data.response) {
                const botMsg = {
                    text: response.data.response,
                    type: 'assistant',
                    createdAt: new Date(),
                };
                setConversationMessages(prev => [...prev, botMsg]);
            }
            
            setSelectedFiles([]);
            setShowUploadModal(false);
            
            setTimeout(() => {
                loadMessageHistory();
            }, 500);
            
        } catch (error) {
            console.error('Erro ao fazer upload:', error);
            
            showNotificationAlert('error', 'Não foi possível fazer\no upload do arquivo!');
        } finally {
            setIsUploading(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64 = reader.result;
                resolve(base64);
            };
            reader.onerror = error => reject(error);
        });
    };

    
    const showNotificationAlert = (type, message) => {
        setNotificationType(type);
        setNotificationMessage(message);
        setShowNotification(true);
        
        
        setTimeout(() => {
            setShowNotification(false);
        }, 3000);
    };

    
    const closeNotification = () => {
        setShowNotification(false);
    };

    const activateIA = async () => {
        try {
            const phoneToUse = getPriorityPhoneNumber();
            
            console.log('Números disponíveis para activateIA:');
            console.log('- confirmedNumber:', pastDueData?.confirmedNumber);
            console.log('- cellPhone:', pastDueData?.cellPhone);
            console.log('- phone (routing):', phone);
            console.log('- Número escolhido:', phoneToUse);
            
            await deleteRouting(phoneToUse, routing.companyId, routing.personalNumber);
            console.log('IA ativada: registro deletado');
            setIaActive(true);
            
            
            setTimeout(() => {
                navigate(-1); 
            }, 2000); 
        } catch (err) {
            console.error('Erro ao ativar IA:', err);
        }
    };

    const deactivateIA = async () => {
        try {
            const phoneToUse = getPriorityPhoneNumber();
            
            console.log('Números disponíveis para deactivateIA:');
            console.log('- confirmedNumber:', pastDueData?.confirmedNumber);
            console.log('- cellPhone:', pastDueData?.cellPhone);
            console.log('- phone (routing):', phone);
            console.log('- Número escolhido:', phoneToUse);
            
            await assumeConversation({
                phone: phoneToUse,
                companyId: routing.companyId,
                personalNumber: routing.personalNumber,
                agentId: routing.agentId,
            });
            console.log('IA desativada: registro criado');
            setIaActive(false);
        } catch (err) {
            console.error('Erro ao desativar IA:', err);
        }
    };

    const toggleIA = async () => {
        if (iaActive) {
            console.log(`Toggle IA acionado. Estado atual: ${iaActive ? 'Ativa' : 'Inativa'}`);
            
            await deactivateIA();
        } else {
            
            await activateIA();
            setShowPopup(true);
            setTimeout(() => setShowPopup(false), 2500);
        }
    };

    return (
        <div className={styles.container}>
            {/* Top Bar */}
            <div className={styles.topBar}>
                <div className={styles.customerInfo}>
                    <div className={styles.avatar}>
                        {(currentUser?.name || '').split(' ').slice(0,2).map(p=>p[0]).join('').toUpperCase() || 'U'}
                    </div>
                    <div>
                        <p className={styles.customerName}>{currentUser?.name || customerName}</p>
                        <p className={styles.customerEmail}>{currentUser?.email || email}</p>
                    </div>
                    {/* {botData && botData.length > 0 && (
                        <div className={styles.botInfo}>
                            <p className={styles.botName}>IA: {botData[0].name}</p>
                            <p className={styles.botNumber}>Número: {botData[0].botNumber}</p>
                        </div>
                    )} */}
                </div>

                <div className={styles.controlsWrapper}>
                    <div className={styles.toggleWrapper}>
                    <button
                        className={`${styles.toggleButton} ${iaActive ? styles.active : ''}`}
                        onClick={toggleIA}
                    >
                        <span className={styles.toggleCircle}></span>
                    </button>
                    <span className={`${styles.toggleText} ${iaActive ? styles.activeText : ''}`}>
                        Ativar IA na conversa
                    </span>
                    </div>
                </div>
            </div>

            {/* Chat */}
            <div ref={chatContainerRef} className={styles.chatContainer}>
                {/* Mensagem de aviso se não há atividade recente */}
                {!hasRecentActivity && (
                    <div style={{ 
                        padding: '10px', 
                        backgroundColor: '#fff3cd', 
                        border: '1px solid #ffeaa7', 
                        borderRadius: '4px', 
                        marginBottom: '10px',
                        color: '#856404',
                        fontSize: '14px',
                        textAlign: 'center',
                        margin: '10px'
                    }}>
                        Número não teve atividade recente nas últimas 24 horas
                    </div>
                )}
                
                {/* Mensagens combinadas (histórico + conversa atual) */}
                {allMessages.map((message, index) => (
                    message.text && message.text.trim() !== '' && (
                        <div
                            key={`message-${index}-${message.createdAt}`}
                            className={`${styles.response} ${message.type === 'answer' ? styles.userMessage : styles.assistantMessage} ${message.source === 'history' ? styles.historyMessage : ''}`}
                            style={{ 
                                alignSelf: message.type === 'answer' ? 'flex-end' : 'flex-start'
                            }}
                        >
                            <div className={styles.messageText}>
                                {message.isFile ? (
                                    <FileMessage 
                                        base64Content={message.text}
                                        fileName={message.fileName || 'arquivo'}
                                        fileType={message.fileType}
                                    />
                                ) : message.type === 'answer' ? (
                                    message.text
                                ) : (
                                    <div dangerouslySetInnerHTML={{ __html: message.text }} />
                                )}
                            </div>
                            <div className={styles.messageDate}>
                                {message.createdAt && moment(message.createdAt).format('HH:mm DD/MM')}
                            </div>
                        </div>
                    )
                ))}
            </div>

            {/* Input */}
            <div className={styles.inputBar}>
                <textarea
                    className={styles.inputMessage}
                    placeholder="Escreva sua mensagem..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onInput={(e) => {
                        e.target.style.height = "auto";
                        e.target.style.height = e.target.scrollHeight + "px";
                    }}
                    onKeyDown={handleInputKeyDown}
                    rows={1}
                    disabled={iaActive}
                />
                <button
                    className={`${styles.attachButton} ${iaActive ? styles.disabledButton : ''}`}
                    onClick={() => setShowUploadModal(true)}
                    disabled={iaActive}
                    title="Anexar arquivos"
                >
                    <IoMdAttach size={18} />
                </button>
                <button
                    className={`${styles.sendButton} ${iaActive || isSending ? styles.disabledButton : ''}`}
                    onClick={handleSendMessage}
                    disabled={iaActive || isSending}
                >
                    {isSending ? (
                        <div className={styles.loadingSpinner}></div>
                    ) : (
                        <IoMdSend size={18} />
                    )}
                </button>
            </div>

            {/* Popup IA */}
            {showPopup && (
                <div className={styles.popup}>
                    <div className={styles.popupContent}>
                        <button className={styles.closePopup} onClick={() => setShowPopup(false)}>×</button>
                        <span className={styles.checkMark}>✅</span>
                        <p>A IA foi ativada com sucesso.<br />Agora você não poderá mais enviar respostas no chat.</p>
                    </div>
                </div>
            )}

            {/* Modal de Upload de Arquivos */}
            {showUploadModal && (
                <div className={styles.uploadModal}>
                    <div className={styles.uploadModalContent}>
                        <div className={styles.uploadModalHeader}>
                            <h3>Upload de arquivos</h3>
                            <button 
                                className={styles.closeUploadModal} 
                                onClick={() => {
                                    setShowUploadModal(false);
                                    setSelectedFiles([]);
                                }}
                            >
                                <IoMdClose size={20} />
                            </button>
                        </div>
                        
                        <div 
                            className={`${styles.uploadArea} ${dragActive ? styles.dragActive : ''}`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                        >
                            <div className={styles.uploadIcon}>
                                <IoMdCloudUpload size={48} />
                            </div>
                            <p className={styles.uploadText}>
                                Clique ou arraste o arquivo aqui
                                <br />
                                <small>Formatos aceitos: PDF, JPEG, PNG (máx. 2MB)</small>
                            </p>
                            <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                                onChange={handleFileSelect}
                                className={styles.fileInput}
                                id="fileInput"
                            />
                            <label htmlFor="fileInput" className={styles.fileInputLabel}>
                                Selecionar arquivo
                            </label>
                        </div>

                        {selectedFiles.length > 0 && (
                            <div className={styles.selectedFiles}>
                                <h4>Arquivo selecionado:</h4>
                                {selectedFiles.map((file, index) => (
                                    <div key={index} className={styles.fileItem}>
                                        <div className={styles.fileInfo}>
                                            <span className={styles.fileName}>{file.name}</span>
                                            <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                                        </div>
                                        <button 
                                            className={styles.removeFileButton}
                                            onClick={() => removeFile(index)}
                                        >
                                            <IoMdClose size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className={styles.uploadActions}>
                            <button 
                                className={styles.cancelButton}
                                onClick={() => {
                                    setShowUploadModal(false);
                                    setSelectedFiles([]);
                                }}
                            >
                                Cancelar
                            </button>
                            <button 
                                className={styles.uploadButton}
                                onClick={handleUpload}
                                disabled={selectedFiles.length === 0 || isUploading}
                            >
                                {isUploading ? (
                                    <>
                                        <div className={styles.loadingSpinner}></div>
                                        Enviando...
                                    </>
                                ) : (
                                    `Enviar ${selectedFiles.length} arquivo(s)`
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Notificação */}
            {showNotification && (
                <div className={styles.notificationOverlay}>
                    <div className={styles.notificationModal}>
                        <button 
                            className={styles.closeNotification}
                            onClick={closeNotification}
                        >
                            ×
                        </button>
                        <div className={styles.notificationIcon}>
                            {notificationType === 'success' ? (
                                <div className={styles.successIcon}>✓</div>
                            ) : (
                                <div className={styles.errorIcon}>×</div>
                            )}
                        </div>
                        <p className={styles.notificationMessage}>
                            {notificationMessage.split('\n').map((line, index) => (
                                <span key={index}>
                                    {line}
                                    {index < notificationMessage.split('\n').length - 1 && <br />}
                                </span>
                            ))}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HumanChat;